'use client';

import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { useFlightStores } from '../../shared/context/flightStoresContext';
import { Section } from '../../../content-sections/ui/components/Section';
import { FlightList } from './list/FlightList';
import Button from '../../../../components/common/button/Button';
import { FlightListShimmer } from './list/shimmer/FlightListShimmer';
import { NoResults } from '../../../../components/common/no-results/NoResults';
import { ErrorMessages } from '../../../../utils/errorMessages';
import {
	ChevronDownIcon,
	ChevronUpIcon,
	Css,
	Responsive,
} from '../../../../ui';
import IAHIllustrationAirplane from '../../../../assets/iah-illustration-airplane.svg';
import HOUIllustrationAirplane from '../../../../assets/hou-illustration-airplane.svg';
import { AirportCodes } from '../../../../types/airports';

interface IProps {
	airportCode?: AirportCodes;
}

export const ButtonWrapper = styled.div`
	display: flex;
	justify-content: center;
`;

export const StyledSection = styled(Section)`
	padding-top: ${Css.pxToRem(32)};
	padding-bottom: ${Css.pxToRem(80)};
`;

const StyledIllustrationWrapper = styled.div`
	width: ${Css.pxToRem(500)};
	height: ${Css.pxToRem(300)};

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: ${Css.pxToRem(240)};
		height: ${Css.pxToRem(133)};
	}
`;

export const FlightsOverview = observer(({ airportCode }: IProps) => {
	const { flightListStore } = useFlightStores();

	useEffect(() => {
		flightListStore.setSelectedAirportCode(airportCode);
	}, [airportCode, flightListStore]);

	const {
		flights,
		isLoading: isFlightsLoading,
		finishedInitialLoad,
		minimumListLength,
		canNavigateBackward,
		scope,
		canNavigateForward,
		showEarlier,
		showLater,
	} = flightListStore;

	const isLoading = isFlightsLoading || !finishedInitialLoad;

	const noResults = flights.length === 0;

	return (
		<StyledSection>
			{isLoading ? (
				<FlightListShimmer listLength={minimumListLength} />
			) : noResults ? (
				<NoResults
					title="No flights found"
					image={
						<StyledIllustrationWrapper>
							{airportCode === AirportCodes.HOU ? (
								<HOUIllustrationAirplane aria-label="Plane icon" />
							) : (
								<IAHIllustrationAirplane aria-label="Plane icon" />
							)}
						</StyledIllustrationWrapper>
					}
					message={ErrorMessages.FlightsNoResults}
				/>
			) : (
				<>
					<ButtonWrapper>
						<Button
							onClick={showEarlier}
							icon={<ChevronUpIcon />}
							buttonType="secondary"
							disabled={!canNavigateBackward}
						>
							Show earlier flights
						</Button>
					</ButtonWrapper>

					<FlightList flights={flights} scope={scope} />

					<ButtonWrapper>
						<Button
							onClick={showLater}
							icon={<ChevronDownIcon />}
							buttonType="secondary"
							disabled={!canNavigateForward}
						>
							Show later flights
						</Button>
					</ButtonWrapper>
				</>
			)}
		</StyledSection>
	);
});
